import { Calendar, MapPin, Users, Eye, Tag } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { formatDate, formatViewCount } from '../../../utils/formatters';
import { getFullAttachmentUrl } from '@services/api/index.js'

/**
 * ActivityCard - 活动卡片组件
 * 展示活动的基本信息，包括封面图、标题、时间、地点等
 */
const ActivityCard = ({ 
  activity, 
  variant = 'default',
  className = ''
}) => {
  const { t } = useTranslation();

  if (!activity) return null;

  const {
    id,
    title,
    category,
    startDate,
    endDate,
    location,
    organizer,
    coverId,
    tags = [],
    description,
    fee
  } = activity;

  // 格式化活动时间显示
  const formatActivityTime = () => {
    if (!startDate) return '';
    
    const start = new Date(startDate);
    const end = endDate ? new Date(endDate) : null;
    
    if (end && start.toDateString() !== end.toDateString()) {
      // 多天活动
      return `${formatDate(start, 'MM.DD')} - ${formatDate(end, 'MM.DD')}`;
    } else {
      // 单天活动
      return formatDate(start, 'YYYY.MM.DD');
    }
  };

  // 获取活动状态
  const getActivityStatus = () => {
    if (!startDate) return 'upcoming';
    
    const now = new Date();
    const start = new Date(startDate);
    const end = endDate ? new Date(endDate) : start;
    
    if (now < start) return 'upcoming';
    if (now > end) return 'ended';
    return 'ongoing';
  };

  const status = getActivityStatus();

  const statusConfig = {
    upcoming: {
      label: t('activities.status.upcoming', '即将开始'),
      className: 'bg-blue-100 text-blue-800'
    },
    ongoing: {
      label: t('activities.status.ongoing', '进行中'),
      className: 'bg-green-100 text-green-800'
    },
    ended: {
      label: t('activities.status.ended', '已结束'),
      className: 'bg-gray-100 text-gray-800'
    }
  };

  const baseClasses = `
    bg-white rounded-lg border border-gray-200 
    hover:border-primary-300 hover:shadow-md
    transition-all duration-200 overflow-hidden
    ${className}
  `;

  return (
    <div className={`${baseClasses} flex flex-col`}>
      {/* 活动封面图 */}
      <div className="aspect-w-16 aspect-h-9 bg-gray-100">
        {/* 使用条件渲染，避免占位图片加载失败导致的无限循环 */}
          {coverId ? (
            <img
              src={getFullAttachmentUrl(coverId)}
              alt={title}
              className="w-full h-48 object-cover"
              loading="lazy"
              onError={(e) => {
                // 移除 onError 事件监听器
                e.target.onerror = null;
                // 当封面图加载失败时，使用占位图片
                e.target.src = '/images/placeholders/activity-placeholder.jpg';
              }}
            />
          ) : (
            // 直接使用占位图片，不添加 onError 处理
            <img
              src='/images/placeholders/activity-placeholder.jpg'
              alt={title}
              className="w-full h-48 object-cover"
              loading="lazy"
            />
          )}
      </div>

      {/* 卡片内容 */}
      <div className="p-3 space-y-2 flex-grow pb-0">
        {/* 分类 */}
        {category && (
          <div className="flex justify-end">
            <span className="text-sm text-primary-600 font-medium">
              {category}
            </span>
          </div>
        )}

        {/* 活动标题 */}
        <h3 className="font-semibold text-gray-900 line-clamp-2 text-lg">
          {title}
        </h3>

        {/* 活动信息 */}
        <div className="space-y-1 text-xs text-gray-600 h-12 flex flex-col justify-center">
          {/* 地点 */}
          {location && (
            <div className="flex items-center">
              <MapPin className="w-3 h-3 mr-1 text-gray-400" />
              <span className="line-clamp-1">{location}</span>
            </div>
          )}

          {/* 主办方 */}
          {organizer && (
            <div className="flex items-center">
              <Users className="w-3 h-3 mr-1 text-gray-400" />
              <span className="line-clamp-1">
                {t('activities.organizer', '主办')}：{organizer}
              </span>
            </div>
          )}
        </div>

        {/* 标签 */}
        {tags.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {tags.slice(0, 3).map((tag, index) => (
              <span
                key={index}
                className="inline-flex items-center px-2 py-1 rounded text-xs bg-gray-100 text-gray-700"
              >
                <Tag className="w-3 h-3 mr-1" />
                {tag}
              </span>
            ))}
            {tags.length > 3 && (
              <span className="text-xs text-gray-500">
                +{tags.length - 3}
              </span>
            )}
          </div>
        )}


      </div>

      {/* 操作按钮 */}
      <div className="p-3 border-t border-gray-100 mt-0 pt-2">
        <Link
          to={`/activities/${id}`}
          className="w-full bg-primary-600 hover:bg-primary-700 text-white text-center py-2 px-4 rounded-md text-sm font-medium transition-colors inline-block"
        >
          {t('activities.actions.viewDetails', '查看详情')}
        </Link>
      </div>
    </div>
  );
};

export default ActivityCard; 